import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Layers, Upload, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardGrid, CardGridSkeleton } from "@/components/cards/CardGrid";
import { EmptyState } from "@/components/common/PageParts";
import { RoadmapSection } from "@/components/roadmap/RoadmapSection";

function Hero({ stats }) {
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden border-b border-amber-900/30" data-testid="home-hero">
      <img src="/images/hero.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D12] via-[#0B0D12]/85 to-[#0B0D12]/30" />
      <div className="container-x relative grid gap-10 py-24 md:grid-cols-12 md:py-32">
        <div className="rise md:col-span-7">
          <p className="eyebrow mb-5">The arcane engineering workshop</p>
          <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight text-amber-100 drop-shadow-[0_2px_12px_rgba(34,211,238,0.15)] sm:text-5xl lg:text-6xl">
            Forge your cards. Build your legends.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Shape the multiverse at The Planar Foundry. Upload custom Magic: The Gathering cards from Magic Set Editor, MTG Cardsmith or any
            tool, organise them into sets, and build Commander or constructed decks that mix custom and official cards.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/library" className="btn-gold" data-testid="hero-explore-button"><Search className="h-4 w-4" />Explore the library</Link>
            <Link to="/decks" className="btn-outline" data-testid="hero-decks-button">Open the deck builder</Link>
            <Link to={user ? "/upload/bulk" : "/register"} className="btn-outline" data-testid="hero-upload-button">
              <Upload className="h-4 w-4" />{user ? "Upload a set" : "Start creating"}
            </Link>
          </div>
        </div>
        <dl className="rise grid grid-cols-2 gap-4 self-end md:col-span-4 md:col-start-9" style={{ animationDelay: "150ms" }} data-testid="home-stats">
          {[["Custom cards", stats?.cards], ["Card backs", stats?.card_backs], ["Sets", stats?.sets], ["Creators", stats?.creators]].map(([label, v]) => (
            <div key={label} className="rounded-xl border border-amber-900/40 bg-[#121620]/80 p-5 backdrop-blur-md">
              <dt className="eyebrow text-[10px]">{label}</dt>
              <dd className="mt-2 font-heading text-3xl text-amber-100">{v ?? "—"}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/meta/stats").then((r) => r.data) });
  const { data: recent, isLoading } = useQuery({
    queryKey: ["cards", "recent"],
    queryFn: () => api.get("/cards", { params: { page_size: 12 } }).then((r) => r.data),
  });

  return (
    <>
      <Hero stats={stats} />
      <section className="container-x py-20" data-testid="home-recent-section">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-3">Fresh from the foundry</p>
            <h2 className="font-heading text-2xl font-bold text-amber-200/90 sm:text-3xl">Recently published</h2>
          </div>
          <Link to="/library" className="btn-ghost" data-testid="home-view-all-link">View all<ArrowRight className="h-4 w-4" /></Link>
        </div>
        {isLoading ? <CardGridSkeleton count={6} /> : recent?.items?.length ? (
          <CardGrid cards={recent.items} testid="home-recent-grid" />
        ) : (
          <EmptyState icon={Layers} title="The library is waiting for its first card" testid="home-empty">
            Be the first creator to publish a custom card.
          </EmptyState>
        )}
      </section>
      <section className="container-x pb-8" data-testid="home-roadmap">
        <p className="eyebrow mb-3">What comes next</p>
        <h2 className="mb-3 font-heading text-2xl font-bold text-amber-200/90 sm:text-3xl">Platform roadmap</h2>
        <p className="mb-10 max-w-2xl text-sm text-slate-400">These features are planned and not yet available. They appear here for transparency only.</p>
        <RoadmapSection compact />
      </section>
    </>
  );
}
